import * as Api from '@ViewModels';
import { IContentCalendarSelectedSuggestion } from '../index';

/**
 * Suggestion sources that should not be sent to bulk AI content generation.
 */
export const isBulkAiExcludedSource = (source?: Api.ContentCalendarSuggestionSource): boolean =>
	source === 'Custom' || source === 'CustomContentTemplate';

/**
 * True when the firm signed this copy off as written and our automation left it that way; customizing it deliberately
 * drops that sign-off.
 */
export const isComplianceReviewedContent = (suggestion: IContentCalendarSelectedSuggestion): boolean =>
	!!suggestion.customizationSkippedForComplianceReview;

/**
 * True when a suggestion may be included in a bulk "Make Content Unique with AI" run.
 */
export const isBulkAiEligible = (suggestion: IContentCalendarSelectedSuggestion): boolean =>
	!isBulkAiExcludedSource(suggestion.source) &&
	!suggestion.customizedForAccount &&
	!isComplianceReviewedContent(suggestion);

/**
 * True when at least one selected suggestion may be included in a bulk "Make Content Unique with AI" run.
 * Empty list returns false (nothing to run).
 */
export const hasAnyBulkAiEligibleSuggestion = (suggestions: IContentCalendarSelectedSuggestion[]): boolean =>
	suggestions.some(isBulkAiEligible);

/**
 * True when a campaign's content already received broker-dealer sign-off before sending; AI editing is blocked once
 * that approval has been granted.
 */
export const wasApprovedForCompliantSend = (
	campaignStatus: Api.EmailSendStatus | undefined,
	approvalRequestsCount: number | undefined,
	sendWithCompliance: boolean | undefined
): boolean => campaignStatus === Api.EmailSendStatus.Queued && !!approvalRequestsCount && sendWithCompliance === true;

/**
 * Social's analog to `wasApprovedForCompliantSend` — `PostStatus.Pending` (not `Scheduled`) is the status used
 * while compliance approval is outstanding, so reaching `Scheduled` with an approval request already means the
 * firm signed off.
 */
export const wasApprovedForCompliantSocialSend = (
	postStatus: Api.PostStatus | undefined,
	approvalRequestsCount: number | undefined,
	sendWithCompliance: boolean | undefined
): boolean => postStatus === Api.PostStatus.Scheduled && !!approvalRequestsCount && sendWithCompliance === true;
